import React, { useState, useMemo } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import { ArrowUp, ArrowDown, AlertTriangle, Info, Check } from "lucide-react";
import { ReconnectionRequest } from "../../types";
import { useReconnectionRequestsWithTab } from "../../hooks";

import { PAGE_SIZE } from "@shared/utils/constants";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import {
  getAddressStringForConsumer,
  renderPriority,
  useCustomFilters,
} from "@features/cx/shared/utils/filters";
import { useNavigate } from "react-router-dom";
import { ReconnectionFilterPanel } from "../filters";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";

export const CurrentList: React.FC = () => {
  const navigate = useNavigate();
  const CURRENCY = useCurrency();
  const remoteUtilityId = useRemoteUtilityId();

  const renderPriority = (value: string) => {
    if (value === "High") {
      return (
        <span
          style={{
            color: "#e53e3e",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <AlertTriangle size={16} style={{ color: "#e53e3e" }} /> High
        </span>
      );
    }
    if (value === "Medium") {
      return (
        <span
          style={{
            color: "#d97706",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <ArrowUp size={16} style={{ color: "#d97706" }} /> Medium
        </span>
      );
    }
    if (value === "Low") {
      return (
        <span
          style={{
            color: "#2563eb",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <ArrowDown size={16} style={{ color: "#2563eb" }} /> Low
        </span>
      );
    }
    if (value === "Normal") {
      return (
        <span
          style={{
            color: "#22c55e",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Check size={16} style={{ color: "#22c55e" }} /> Normal
        </span>
      );
    }

    if (value === "Critical") {
      return (
        <span
          style={{
            color: "#dc2626",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <ArrowUp size={16} style={{ color: "#dc2626" }} /> Critical
        </span>
      );
    }
    return value;
  };

  const columns: TableColumn<ReconnectionRequest>[] = useMemo(
    () => [
      {
        key: "requestId",
        header: "Request ID",
        type: "text",
        render: (value, row) => (
          <div>
            <p className="font-medium">{value ?? "NA"}</p>
            <p className="text-xs text-muted-foreground">
              {row.requestType ?? "NA"}
            </p>
          </div>
        ),
      },
      // { key: "customerName", header: "Customer", type: "text" },
      // { key: "accountNumber", header: "Account Number", type: "text" },
      {
        key: "customerName",
        header: "Consumer",
        render: (value, row) => (
          <div>
            <p className="font-medium">{value ?? "NA"}</p>
            <p className="text-xs text-muted-foreground">
              {row.accountNumber ?? "NA"}
            </p>
          </div>
        ),
      },
      { key: "name", header: "Reason", type: "text" },
      { key: "dateRequested", header: "Preferred Date", type: "text" },
      {
        key: "status",
        header: "Status",
        type: "badge",
        badgeConfig: {
          getVariant: (value: string) => {
            if (value === "pending") return "secondary";
            if (value === "scheduled") return "default";
            if (value === "completed") return "outline";
            if (value === "cancelled") return "destructive";
            return "default";
          },
        },
      },
      { key: "serviceOrderStatus", header: "SO Status", type: "badge" },
      {
        key: "createdDate",
        header: "Created On",
        render: (value, row) => (
          <div>
            <p className="font-medium">{value ?? "NA"}</p>
            <p className="text-xs text-muted-foreground">
              {row.createdUserRemoteName ?? "NA"}
            </p>
          </div>
        ),
      },

      {
        key: "outstandingBalance",
        header: "Outstanding",
        type: "custom",
        render: (value) => `${CURRENCY} ${value}`,
      },
      {
        key: "priorityLevelDisplay",
        header: "Priority",
        type: "custom",
        render: (value) => renderPriority(value),
      },
    ],
    [renderPriority],
  );

  const actions: TableAction<ReconnectionRequest>[] = useMemo(
    () => [
      {
        label: "View",
        onClick: (item) => {
          if (item.requestType?.toLowerCase() === "bulk") {
            navigate(`/cx/reconnections/bulk/view/current/${item.id}`);
          } else {
            navigate(`/cx/reconnections/view/current/${item.id}`);
          }
        },
      },
    ],
    [navigate],
  );

  const filterConfig = useMemo(
    () => ({
      paramNames: {
        status: "status",
        category: "category",
        search: "search_data",
        page: "page",
        name: "name",
        priority: "priority",
      },
      defaults: {
        page: 1,
        limit: 50,
        status: [],
        category: "ALL_CATEGORIES",
        search: "",
        name: "name",
        priority: [],
      },
      parsers: {
        status: (value: string) => value.split(",").map(Number),
        category: (value: string) => value,
        search: (value: string) => value.trim(),
        priority: (value: string) => value.split(",").map(Number),
      },
      serializers: {
        status: (value: number[]) => value.join(","),
        category: (value: string) => value,
        search: (value: string) => value.trim(),
        priority: (value: number[]) => value.join(","),
      },
    }),
    [],
  );

  const {
    filters,
    page,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleSearch,
  } = useCustomFilters(filterConfig, Number(remoteUtilityId));

  const { data } = useReconnectionRequestsWithTab(
    Number(remoteUtilityId),
    "Current",
    {
      page: filters.page,
      limit: PAGE_SIZE,
      searchData: filters.search_data,
      status: filters.status?.join(","),
      remoteUtilityId: Number(remoteUtilityId),
    },
  );

  const transformedData = useMemo(
    () =>
      data?.results?.map((item: ReconnectionRequest) => ({
        id: item.id,
        requestId: item.requestId,
        customerName: item.consumer.name,
        accountNumber: item.consumer.consumerNo,
        serviceAddress: item.consumer?.serviceAddress,
        type: item.requestTypeDisplay,
        dateRequested: item.preferredDate,
        status: item.status,
        outstandingBalance: item.outstandingBalance,
        totalOutstanding: item.additionalData?.totalOutstanding,
        priorityLevelDisplay: item.priority,
        requestType: item.type,
        createdDate: item.createdOn,
        createdUserRemoteName: item.createdUserRemoteName,
        name: item?.reason,
        category: item.supportRequestType,
        subCategory: item.supportRequestSubType,
        serviceOrderStatus: item.serviceOrderStatus

      })) || [],
    [data?.results],
  );

  const totalItems = data?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  return (
    <GenericTable
      data={transformedData as unknown as ReconnectionRequest[]}
      columns={columns}
      rowKey="id"
      searchPlaceholder="Search by name, email, or ID..."
      searchParamName="query"
      searchDebounceMs={300}
      onSearch={handleSearch}
      pagination={{
        currentPage: page,
        totalPages,
        totalItems,
        itemsPerPage: PAGE_SIZE,
        startItem: (page - 1) * PAGE_SIZE + 1,
        endItem: Math.min(page * PAGE_SIZE, totalItems),
      }}
      onPageChange={handlePageChange}
      actions={actions}
      actionsType="dropdown"
      filterPanel={
        <ReconnectionFilterPanel
          currentFilters={filters}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
          tab={2}
        />
      }
    />
  );
};
